import { useCallback, useMemo } from 'react';

import { DashboardProps } from '@/modules/tasks/views/TasksDashboard/components/TaskDashboard/types';
import {
  ActionItemPriorityEnum,
  CreateActionItemInput,
  RelatedEntityInput,
  StatusType,
} from '@/types/schema';

import { ActionItemFragment } from '../graphql/fragments/generated/ActionItem';
import { ActionItemActions } from '../types';
import useActionItemsAction from './useActionItemAction';

const useActionItemActions = ({
  entity,
  onRefetchParentProgress,
  inViewFileId,
  isSystemGeneratedFile = false,
}: {
  entity?: RelatedEntityInput;
  onRefetchParentProgress?: DashboardProps['onRefetchParentProgress'];
  inViewFileId?: string;
  isSystemGeneratedFile?: boolean;
}) => {
  const { onAction } = useActionItemsAction({
    entity,
    onRefetchParentProgress,
    inViewFileId,
    isSystemGeneratedFile,
  });

  const onAddActionItem = useCallback(
    (input?: Partial<ActionItemFragment>) => {
      return onAction({
        action: ActionItemActions.Add,
        data: { id: '', ...input },
      }).then((response) => {
        onRefetchParentProgress?.();

        return response;
      });
    },
    [onAction, onRefetchParentProgress],
  );

  const onAddMultipleActionItem = useCallback(
    (actionItems?: CreateActionItemInput[]) => {
      return onAction({
        action: ActionItemActions.ADD_MULTIPLE,
        data: { id: '', actionItems },
      }).then((response) => {
        onRefetchParentProgress?.();

        return response;
      });
    },
    [onAction, onRefetchParentProgress],
  );

  const onMenuItemClick = useCallback(
    (action: ActionItemActions, data: Partial<ActionItemFragment> & { id: string }) =>
      onAction({ action, data }),
    [onAction],
  );

  const onUpdateTitle = useCallback(
    (title: string, id: string) => {
      return onAction({
        action: ActionItemActions.UpdateText,
        data: { title, id },
      });
    },
    [onAction],
  );

  const onToggle = useCallback(
    (status: string, id: string) => {
      return onAction({
        action: ActionItemActions.Toggle,
        data: { id, status },
      });
    },
    [onAction],
  );

  const onAssignClick = useCallback(
    (assignedToId: string, id: string) => {
      return onAction({
        action: ActionItemActions.UpdateAssignedTo,
        data: { assignedToId, id },
      });
    },
    [onAction],
  );

  const onAssignMultipleClick = useCallback(
    (assignedToIds: string[], id: string) => {
      return onAction({
        action: ActionItemActions.UpdateAssignedToMultiple,
        data: { assignedToIds, id },
      });
    },
    [onAction],
  );

  const onDueDateClick = useCallback(
    (selectedTime: string | null, id: string) => {
      return onAction({
        action: ActionItemActions.UpdateDate,
        data: { dueDate: selectedTime, id },
      });
    },
    [onAction],
  );

  const onPriorityChange = useCallback(
    (priority: ActionItemPriorityEnum, id: string) => {
      return onAction({
        action: ActionItemActions.UpdatePriority,
        data: { priority, id },
      });
    },
    [onAction],
  );

  const onFileChange = useCallback(
    (fileId: string, id: string) => {
      return onAction({
        action: ActionItemActions.UpdateFile,
        data: { fileId, id },
      });
    },
    [onAction],
  );

  const onOrderChange = useCallback(
    (order: number, id: string) => {
      return onAction({
        action: ActionItemActions.UpdateOrder,
        data: { order, id },
      });
    },
    [onAction],
  );
  const onStatusChange = useCallback(
    (statusInfo: Pick<StatusType, 'id' | 'name' | 'category'>, id?: string) => {
      return onAction({
        action: ActionItemActions.UpdateStatus,
        data: {
          statusId: statusInfo.id,
          status: statusInfo.name,
          category: statusInfo.category,
          id: id!,
        },
      });
    },
    [onAction],
  );

  const onDeleteActionItem = useCallback(
    (id: string) => {
      return onAction({
        action: ActionItemActions.Delete,
        data: { id },
      });
    },
    [onAction],
  );

  const onStageChange = useCallback(
    (stageIds: string[] | undefined, id: string) => {
      return onAction({
        action: ActionItemActions.UpdateStage,
        data: { stageIds, id },
      });
    },
    [onAction],
  );

  const actions = useMemo(() => {
    return {
      onAddActionItem,
      onMenuItemClick,
      onUpdateTitle,
      onToggle,
      onAssignClick,
      onDueDateClick,
      onPriorityChange,
      onStatusChange,
      onDeleteActionItem,
      onAddMultipleActionItem,
      onOrderChange,
      onFileChange,
      onAssignMultipleClick,
      onStageChange,
    };
  }, [
    onAddActionItem,
    onMenuItemClick,
    onUpdateTitle,
    onToggle,
    onAssignClick,
    onDueDateClick,
    onPriorityChange,
    onStatusChange,
    onDeleteActionItem,
    onAddMultipleActionItem,
    onOrderChange,
    onFileChange,
    onAssignMultipleClick,
    onStageChange,
  ]);

  return {
    onAddActionItem,
    actions,
  };
};

export default useActionItemActions;
